import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Save, Plus, Trash2, Thermometer, Camera, CheckSquare, Video, Mic, FileText, Eye, Settings } from 'lucide-react';
import { MediaRequirementsEditor } from '@/components/MediaRequirementsEditor';

interface TemplateItem {
  id: string;
  type: string;
  label: string;
  description?: string;
  required: boolean;
  min_value?: number;
  max_value?: number;
  unit?: string;
  sauce_options?: string[];
  category?: 'hot_holding' | 'cold_holding' | 'refrigeration' | 'kitchen_cleaning' | 'restaurant_cleaning' | 'bar_cleaning';
  subcategory?: 'fridges' | 'freezers';
  supports_photos?: boolean;
  requires_photos?: boolean;
  supports_comments?: boolean;
  requires_comments?: boolean;
  min_photos?: number;
  max_photos?: number;
}

interface Template {
  id: string;
  name: string;
  description: string | null;
  frequency: string | null;
  is_active: boolean;
  items: TemplateItem[];
}

const TemplateEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);

  // Predefined sauce options for easy selection
  const availableSauces = [
    'Pepper Sauce', 'Birria Sauce', 'Taco Sauce', 'Bolognese', 'Fish Soup', 'BBQ Sauce',
    'Béarnaise Sauce', 'Mango Salsa', 'Mango Sauce', 'Chipotle Aioli', 'Hamburger Dressing',
    'Salsa', 'Guacamole', 'Remoulade', 'Pea Purée', 'Carbonara Sauce', 'Curry Sauce',
    'Sweet & Sour Sauce', 'Hollandaise', 'Mushroom Sauce', 'Garlic Aioli', 'Pesto',
    'Marinara', 'Ranch Dressing', 'Caesar Dressing', 'Buffalo Sauce', 'Teriyaki Sauce'
  ];

  useEffect(() => {
    if (id) {
      fetchTemplate();
    }
  }, [id, user]);

  const fetchTemplate = async () => {
    if (!user || !id) return;

    try {
      const { data, error } = await supabase
        .from('checklist_templates')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      setTemplate({
        ...data,
        items: Array.isArray(data.items) ? data.items as unknown as TemplateItem[] : []
      });
    } catch (error: any) {
      toast({
        title: "Error loading template",
        description: error.message,
        variant: "destructive",
      });
      navigate('/templates');
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!template || !user) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('checklist_templates')
        .update({
          name: template.name,
          description: template.description,
          frequency: template.frequency,
          items: template.items as any,
          is_active: template.is_active
        })
        .eq('id', template.id);

      if (error) throw error;

      toast({
        title: "Template saved!",
        description: "Your template has been updated successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error saving template",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateTemplateField = (field: keyof Template, value: any) => {
    if (!template) return;
    setTemplate({ ...template, [field]: value });
  };

  const updateItem = (itemId: string, updates: Partial<TemplateItem>) => {
    if (!template) return;
    
    setTemplate({
      ...template,
      items: template.items.map(item =>
        item.id === itemId ? { ...item, ...updates } : item
      )
    });
  };

  const addSauceOption = (itemId: string, sauce: string) => {
    const item = template?.items.find(i => i.id === itemId);
    if (!item) return;

    const normalizedSauce = sauce.trim();
    const currentOptions = (item.sauce_options || []).map(s => s.trim());
    const exists = currentOptions.some(s => s.toLowerCase() === normalizedSauce.toLowerCase());

    if (!exists && normalizedSauce) {
      updateItem(itemId, {
        sauce_options: [...currentOptions, normalizedSauce]
      });
    }
  };

  const removeSauceOption = (itemId: string, sauce: string) => {
    const item = template?.items.find(i => i.id === itemId);
    if (!item) return;

    updateItem(itemId, {
      sauce_options: (item.sauce_options || []).filter(s => s !== sauce)
    });
  };

  const addNewItem = () => {
    if (!template) return;
    
    const newItem: TemplateItem = {
      id: `item_${Date.now()}`,
      type: 'temperature',
      label: 'New Temperature Item',
      description: '',
      required: false,
      unit: '°C',
      min_value: undefined,
      max_value: undefined,
      sauce_options: [],
      category: undefined,
      subcategory: undefined
    };

    setTemplate({
      ...template,
      items: [...template.items, newItem]
    });
    setEditingItem(newItem.id);
  };

  const deleteItem = (itemId: string) => {
    if (!template) return;
    
    setTemplate({
      ...template,
      items: template.items.filter(item => item.id !== itemId)
    });
  };

  const getItemIcon = (type: string) => {
    switch (type) {
      case 'temperature':
        return <Thermometer className="h-4 w-4 text-blue-500" />;
      case 'photo':
        return <Camera className="h-4 w-4 text-green-500" />;
      case 'video':
        return <Video className="h-4 w-4 text-purple-500" />;
      case 'voice_note':
        return <Mic className="h-4 w-4 text-orange-500" />;
      case 'checklist':
        return <CheckSquare className="h-4 w-4 text-blue-500" />;
      case 'visual':
        return <Eye className="h-4 w-4 text-purple-500" />;
      case 'checkbox':
        return <CheckSquare className="h-4 w-4 text-orange-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading template...</p>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-crystal-white mb-2">Template not found</h2>
          <Link to="/templates">
            <Button className="cta-primary">Back to Templates</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent/20">
      {/* Header */}
      <header className="glass-nav">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <Link to="/templates" className="text-sm text-muted-foreground hover:text-crystal-white flex items-center gap-2 mb-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Templates
            </Link>
            <h1 className="text-2xl font-bold text-crystal-white">Edit Template</h1>
            <p className="text-sm text-muted-foreground">Modify template items and sauce options</p>
          </div>
          <Button 
            onClick={saveTemplate}
            disabled={saving}
            className="cta-primary"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Template Info */}
          <Card className="glass-panel">
            <CardHeader>
              <CardTitle>Template Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name</Label>
                <Input
                  id="name"
                  value={template.name}
                  onChange={(e) => updateTemplateField('name', e.target.value)}
                  className="glass-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={template.description || ''}
                  onChange={(e) => updateTemplateField('description', e.target.value)}
                  className="glass-input"
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="frequency">Frequency</Label>
                <select
                  id="frequency"
                  value={template.frequency || 'daily'}
                  onChange={(e) => updateTemplateField('frequency', e.target.value)}
                  className="w-full glass-input"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="as_needed">As Needed</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Template Items */}
          <Card className="glass-panel">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Checklist Items ({template.items.length})</CardTitle>
                <CardDescription>
                  Configure sauce options and categories for template items.
                </CardDescription>
              </div>
              <Button onClick={addNewItem} className="cta-primary">
                <Plus className="h-4 w-4 mr-2" />
                Add New Item
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {template.items.map((item) => (
                  <Card key={item.id} className="border border-accent/20">
                    <CardHeader className="pb-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {getItemIcon(item.type)}
                            <div className="flex-1">
                              {editingItem === item.id ? (
                                <div className="space-y-2">
                                  <Input
                                    value={item.label}
                                    onChange={(e) => updateItem(item.id, { label: e.target.value })}
                                    className="glass-input"
                                    placeholder="Item label"
                                  />
                                  <Input
                                    value={item.description || ''}
                                    onChange={(e) => updateItem(item.id, { description: e.target.value })}
                                    className="glass-input"
                                    placeholder="Item description"
                                  />
                                </div>
                              ) : (
                                <div>
                                  <h3 className="font-medium text-crystal-white">{item.label}</h3>
                                  <p className="text-sm text-muted-foreground">{item.description}</p>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {item.type}
                            </Badge>
                            {item.required && (
                              <Badge variant="destructive" className="text-xs">
                                Required
                              </Badge>
                            )}
                            {item.category && (
                              <Badge variant="secondary" className="text-xs">
                                {item.category.replace('_', ' ')}
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteItem(item.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      
                      {/* Item Configuration */}
                      {editingItem === item.id ? (
                        <div className="space-y-4 border border-accent/20 rounded-lg p-4 bg-accent/5">
                          
                          {/* Media Requirements */}
                          <div className="col-span-full">
                            <div className="flex items-center gap-2 mb-2">
                              <Settings className="h-4 w-4" />
                              <Label className="text-sm font-medium">Evidence Requirements</Label>
                            </div>
                            <MediaRequirementsEditor
                              supports_photos={item.supports_photos}
                              requires_photos={item.requires_photos}
                              supports_comments={item.supports_comments}
                              requires_comments={item.requires_comments}
                              min_photos={item.min_photos}
                              max_photos={item.max_photos}
                              onUpdate={(updates) => updateItem(item.id, updates)}
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Category Selection */}
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Section</Label>
                              <select
                                value={item.category || ''}
                                onChange={(e) => {
                                  const category = e.target.value as any;
                                  const updates: Partial<TemplateItem> = { category: category || undefined };
                                  
                                  // Set sensible defaults based on category
                                  if (category === 'hot_holding' && !item.min_value) {
                                    updates.min_value = 55;
                                  } else if (category === 'cold_holding' && !item.max_value) {
                                    updates.min_value = 1;
                                    updates.max_value = 4;
                                  }
                                  
                                  updateItem(item.id, updates);
                                }}
                                className="w-full glass-input"
                              >
                                <option value="">Select Section</option>
                                <option value="hot_holding">🔥 Hot Holding</option>
                                <option value="cold_holding">❄️ Cold Holding</option>
                                <option value="refrigeration">🧊 Refrigeration</option>
                                <option value="kitchen_cleaning">👨‍🍳 Kitchen Cleaning</option>
                                <option value="restaurant_cleaning">🍽️ Restaurant Cleaning</option>
                                <option value="bar_cleaning">🍸 Bar Cleaning</option>
                              </select>
                            </div>

                            {/* Subcategory for Refrigeration */}
                            {item.category === 'refrigeration' && (
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Refrigeration Type</Label>
                                <select
                                  value={item.subcategory || ''}
                                  onChange={(e) => updateItem(item.id, { subcategory: e.target.value as any })}
                                  className="w-full glass-input"
                                >
                                  <option value="">Select Type</option>
                                  <option value="fridges">Fridges</option>
                                  <option value="freezers">Freezers</option>
                                </select>
                              </div>
                            )}

                            {/* Temperature Settings for temperature items */}
                            {item.type === 'temperature' && (
                              <>
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium">Min Temperature</Label>
                                  <Input
                                    type="number"
                                    value={item.min_value || ''}
                                    onChange={(e) => updateItem(item.id, { min_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                                    className="glass-input"
                                    placeholder="Min value"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium">Max Temperature</Label>
                                  <Input
                                    type="number"
                                    value={item.max_value || ''}
                                    onChange={(e) => updateItem(item.id, { max_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                                    className="glass-input"
                                    placeholder="Max value"
                                  />
                                </div>
                              </>
                            )}

                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Unit</Label>
                              <Input
                                value={item.unit || ''}
                                onChange={(e) => updateItem(item.id, { unit: e.target.value })}
                                className="glass-input"
                                placeholder="Unit (e.g., °C)"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={item.required}
                                  onChange={(e) => updateItem(item.id, { required: e.target.checked })}
                                />
                                Required Item
                              </Label>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Temperature Details Display */
                        item.type === 'temperature' && (
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {item.min_value && (
                              <span>Min: {item.min_value}{item.unit}</span>
                            )}
                            {item.max_value && (
                              <span>Max: {item.max_value}{item.unit}</span>
                            )}
                            {item.category && (
                              <span>Section: {item.category.replace('_', ' ')}</span>
                            )}
                          </div>
                        )
                      )}
                    </CardHeader>
                    
                    {/* Sauce Options Section - Only for temperature items */}
                    {item.type === 'temperature' && (
                      <CardContent className="pt-0">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">Sauce Options</Label>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  // Toggle sauce editing - separate from main editing
                                  const sauceEditKey = `${item.id}_sauces`;
                                  setEditingItem(editingItem === sauceEditKey ? null : sauceEditKey);
                                }}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                {editingItem === `${item.id}_sauces` ? 'Done' : 'Add Sauces'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingItem(editingItem === item.id ? null : item.id)}
                              >
                                {editingItem === item.id ? 'Done Editing' : 'Edit Item'}
                              </Button>
                            </div>
                          </div>
                          
                          {/* Current Sauce Options */}
                          {item.sauce_options && item.sauce_options.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {item.sauce_options.map((sauce) => (
                                <Badge key={sauce} variant="secondary" className="flex items-center gap-1">
                                  {sauce}
                                  <button
                                    onClick={() => removeSauceOption(item.id, sauce)}
                                    className="ml-1 hover:text-destructive"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          )}
                          
                          {/* Add Sauce Options */}
                          {editingItem === `${item.id}_sauces` && (
                            <div className="border border-accent/20 rounded-lg p-3 bg-accent/5">
                              <Label className="text-sm font-medium mb-2 block">Available Sauces</Label>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                                {availableSauces
                                  .filter(sauce => !(item.sauce_options || []).includes(sauce))
                                  .map((sauce) => (
                                    <Button
                                      key={sauce}
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => addSauceOption(item.id, sauce)}
                                      className="justify-start text-left h-auto py-1 px-2 text-xs"
                                    >
                                      + {sauce}
                                    </Button>
                                  ))}
                              </div>
                            </div>
                          )}
                          
                          {(!item.sauce_options || item.sauce_options.length === 0) && editingItem !== `${item.id}_sauces` && (
                            <p className="text-sm text-muted-foreground">
                              No sauce options configured. Click "Add Sauces" to add options.
                            </p>
                          )}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default TemplateEditor;